import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, Building2, MapPin, Users, Award, ChevronDown } from 'lucide-react';
import { SUPPLIERS } from '@/data/suppliers';
import { CATEGORIES, filterSuppliersByCategory, searchSuppliers } from '@/lib/index';
import { SupplierCard } from '@/components/SupplierCard';
import { AdBanner } from '@/components/AdBanner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';
import { IMAGES } from '@/assets/images';

export default function Suppliers() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  const countries = useMemo(() => {
    const uniqueCountries = Array.from(new Set(SUPPLIERS.map(s => s.country)));
    return uniqueCountries.sort();
  }, []);

  const filteredSuppliers = useMemo(() => {
    let result = SUPPLIERS;
    
    if (searchQuery) {
      result = searchSuppliers(result, searchQuery);
    }
    
    if (selectedCategory !== 'all') {
      result = filterSuppliersByCategory(result, selectedCategory);
    }
    
    if (selectedCountry !== 'all') {
      result = result.filter(s => s.country === selectedCountry);
    }
    
    return result;
  }, [searchQuery, selectedCategory, selectedCountry]);

  const totalPages = Math.ceil(filteredSuppliers.length / itemsPerPage);
  const paginatedSuppliers = filteredSuppliers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    setCurrentPage(1);
  };

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="relative bg-gradient-to-b from-primary/5 via-background to-background py-16 md:py-24">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.AIRPORT_TERMINAL_2}
            alt="Airport Terminal"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="text-center max-w-3xl mx-auto mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
              Supplier Directory
            </h1>
            <p className="text-lg text-muted-foreground">
              Connect with leading airport security suppliers across Asia Pacific
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.1 }}
            className="max-w-4xl mx-auto"
          >
            <Card className="p-6 bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-card/60 border-border shadow-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search suppliers..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="pl-10"
                  />
                </div>

                <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {CATEGORIES.map(category => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedCountry} onValueChange={handleCountryChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Countries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    {countries.map(country => (
                      <SelectItem key={country} value={country}>
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  {filteredSuppliers.length} {filteredSuppliers.length === 1 ? 'supplier' : 'suppliers'} found
                </p>
                {(searchQuery || selectedCategory !== 'all' || selectedCountry !== 'all') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('all');
                      setSelectedCountry('all');
                      setCurrentPage(1);
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </Card>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <motion.aside
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={springPresets.gentle}
            className="lg:col-span-1"
          >
            <Card className="p-6 sticky top-24">
              <div className="flex items-center gap-2 mb-6">
                <Filter className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-semibold">Filter By</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Categories
                  </h3>
                  <div className="space-y-2">
                    {CATEGORIES.map(category => {
                      const count = SUPPLIERS.filter(s => 
                        s.categories.some(cat => cat.toLowerCase().includes(category.id.toLowerCase()))
                      ).length;
                      return (
                        <button
                          key={category.id}
                          onClick={() => handleCategoryChange(category.id)}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                            selectedCategory === category.id
                              ? 'bg-primary text-primary-foreground'
                              : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{category.name}</span>
                            <Badge variant="secondary" className="ml-2">
                              {count}
                            </Badge>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="pt-6 border-t border-border">
                  <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Location
                  </h3>
                  <div className="space-y-2">
                    {countries.map(country => {
                      const count = SUPPLIERS.filter(s => s.country === country).length;
                      return (
                        <button
                          key={country}
                          onClick={() => handleCountryChange(country)}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                            selectedCountry === country
                              ? 'bg-primary text-primary-foreground'
                              : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{country}</span>
                            <Badge variant="secondary" className="ml-2">
                              {count}
                            </Badge>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Card>

            <div className="mt-6">
              <AdBanner size="rectangle" position="sidebar" />
            </div>
          </motion.aside>

          <div className="lg:col-span-3">
            {paginatedSuppliers.length > 0 ? (
              <>
                <motion.div
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                  className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8"
                >
                  {paginatedSuppliers.map(supplier => (
                    <motion.div key={supplier.id} variants={staggerItem}>
                      <SupplierCard supplier={supplier} />
                    </motion.div>
                  ))}
                </motion.div>

                {totalPages > 1 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ ...springPresets.gentle, delay: 0.3 }}
                    className="flex items-center justify-center gap-2"
                  >
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <div className="flex items-center gap-2">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <Button
                          key={page}
                          variant={currentPage === page ? 'default' : 'outline'}
                          onClick={() => setCurrentPage(page)}
                          className="w-10"
                        >
                          {page}
                        </Button>
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </motion.div>
                )}
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={springPresets.gentle}
              >
                <Card className="p-12 text-center">
                  <Building2 className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-xl font-semibold mb-2">No suppliers found</h3>
                  <p className="text-muted-foreground mb-6">
                    Try adjusting your filters or search query
                  </p>
                  <Button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('all');
                      setSelectedCountry('all');
                      setCurrentPage(1);
                    }}
                  >
                    Clear All Filters
                  </Button>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-b from-background to-primary/5 py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
          >
            <Card className="relative overflow-hidden">
              <div className="absolute inset-0 z-0">
                <img
                  src={IMAGES.BUSINESS_MEETING_7}
                  alt="Business Meeting"
                  className="w-full h-full object-cover opacity-20"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-accent/90" />
              </div>

              <div className="relative z-10 p-12 text-center">
                <Award className="h-16 w-16 mx-auto mb-6 text-primary-foreground" />
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-primary-foreground">
                  List Your Company
                </h2>
                <p className="text-lg text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
                  Join our directory of leading airport security suppliers and connect with airports across Asia Pacific
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button size="lg" variant="secondary" className="min-w-[200px]">
                    Add Your Company
                  </Button>
                  <Button size="lg" variant="outline" className="min-w-[200px] bg-white/10 text-primary-foreground border-primary-foreground/20 hover:bg-white/20">
                    Learn More
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <AdBanner size="leaderboard" position="bottom" />
      </div>
    </div>
  );
}
